import { useEffect, useRef } from 'react'

/**
 * A confirmation the page owns, instead of window.confirm.
 *
 * The browser's dialog cannot be styled, says "localhost:5173 says" above the
 * question, and blocks the whole tab while it is open. This one carries the
 * app's own weights — the destructive action in red, the way out first — and
 * behaves like a dialog should: Escape and the backdrop cancel, focus moves
 * into it and returns to whatever opened it.
 *
 * Controlled on purpose: the caller holds what is being confirmed, so the
 * dialog has no idea what a unit or a role is.
 */
export default function Confirm({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
  // Optional extra content under the message, such as a reason box.
  children,
}) {
  const confirmRef = useRef(null)
  const openerRef = useRef(null)

  // Same reason as Drawer: an inline arrow prop would re-run this on every
  // render of the page behind it.
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  useEffect(() => {
    if (!open) return undefined

    openerRef.current = document.activeElement
    confirmRef.current?.focus()

    const onKey = (event) => {
      if (event.key === 'Escape') onCancelRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    // Without this the page behind scrolls under the dialog.
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      // Put the caret back where the person left it.
      openerRef.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel?.()
      }}
    >
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 className="modal-title" id="confirm-title">
          {title}
        </h2>
        {message && <p className="modal-message">{message}</p>}
        {children}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn ${tone === 'danger' ? 'btn-danger-solid' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
